/**
 * Bereich *Energie* (Spec 45, ADR 0033; gefüllt mit Spec 46a) — Route
 * `/energie`. Container: besitzt die Abfragen (`GET /energy/meters`, bei
 * geöffnetem Detail `GET /energy/meters/{id}`) und die Blätter, die Kinder
 * bekommen Props.
 *
 * Haus-Filter und geöffnetes Zähler stehen in der URL (`?haus=`, `?zaehler=`).
 * Ab 768 px sitzt der Filter in der Kontext-Sidebar (`router.tsx::
 * EnergieSidebar`, liest dieselbe Abfrage aus dem Cache), darunter als
 * Chipzeile über der Liste. Ab 1024 px steht das Detail rechts neben der
 * Liste (460 px), darunter ersetzt es sie (`meter-detail-back`).
 *
 * Spec 46c: über der Liste „Demnächst kündbar" (`cancellable` derselben
 * Antwort); im Detail Liefervertrag und Dokumente am Zähler. Die Blätter des
 * Vertrags öffnet dieser Container (`EnergieSupply`), die Dokumentliste
 * (`features/documents`) reicht er als Platz in das Detail.
 *
 * Die Öl-/Gaspreis-Kapsel bleibt iOS (Spec 45, R1). Unter 768 px ist
 * `/energie` nicht über die mobile Tab-Leiste erreichbar (drei Einstiege,
 * ADR 0019), nur per URL — dieselbe Seite.
 */

import { useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import { ConfirmationStrip } from '@/components/ConfirmationStrip'
import { useInfiniteDocumentsList } from '@/features/documents/api'
import { DocumentList } from '@/features/documents/DocumentList'
import { useMeter, useMeters } from '@/features/energy/api'
import { resolveHouse } from '@/features/energy/house-filter'
import { MeterDetail } from '@/features/energy/MeterDetail'
import { MeterList } from '@/features/energy/MeterList'
import { MeterCreateSheet } from '@/features/energy/MeterSheet'
import { useIsDesktopViewport } from '@/lib/viewport'
import { SupplyContractSheets } from '@/routes/EnergieSupply'
import type { SupplySheetState } from '@/routes/EnergieSupply'

export function Energie() {
  const navigate = useNavigate({ from: '/energie' })
  const search = useSearch({ from: '/energie' })
  const isDesktop = useIsDesktopViewport()
  const meters = useMeters(search.haus)
  const detail = useMeter(search.zaehler)
  // Dokumente am Zähler: dieselbe Abfrage wie der Reiter *Dokumente* eines Objekts
  // (`?scope=`), hier mit dem Zähler als Ausschnitt (Spec 46c, R11).
  const documents = useInfiniteDocumentsList(search.zaehler === undefined ? {} : { scope: search.zaehler }, { enabled: search.zaehler !== undefined })
  const [supplySheet, setSupplySheet] = useState<SupplySheetState | null>(null)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [confirmation, setConfirmation] = useState<{ readonly message: string; readonly testId: string } | null>(null)

  const houses = meters.data?.houses
  // Ein Haus, das es nicht (mehr) gibt, fällt auf „Alle" zurück — in der URL.
  const isStaleHouse = houses !== undefined && search.haus !== undefined && resolveHouse(houses, search.haus) === undefined
  useEffect(() => {
    if (isStaleHouse) void navigate({ search: (previous) => ({ ...previous, haus: undefined }), replace: true })
  }, [isStaleHouse, navigate])

  const presetHouse = houses?.find((house) => house.id === search.haus)
  const hasDetail = search.zaehler !== undefined

  return (
    <section aria-labelledby="energie-heading" data-testid="scope-energie" className="flex h-full flex-col">
      <header className="border-border flex min-h-(--row-h) items-center justify-between gap-3 border-b px-(--page-x) py-1">
        <h1 id="energie-heading" className="text-page font-(--font-weight-page)">
          Energie
        </h1>
        {/* Die eine primäre Aktion des Bereichs. */}
        <button
          type="button"
          data-testid="energy-meter-add"
          onClick={() => {
            setIsCreateOpen(true)
          }}
          className="bg-accent rounded-card text-body shrink-0 px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out)"
        >
          Zähler anlegen
        </button>
      </header>

      <div className="flex min-h-0 flex-1">
        <div
          className={`min-h-0 overflow-y-auto ${
            hasDetail ? 'border-border max-lg:hidden lg:w-[460px] lg:shrink-0 lg:border-r' : 'flex-1'
          }`}
        >
          {meters.isPending || isStaleHouse ? (
            <MeterList state="loading" showsChips={!isDesktop} />
          ) : meters.isError ? (
            <MeterList
              state="error"
              error={meters.error}
              onRetry={() => {
                void meters.refetch()
              }}
            />
          ) : (
            <MeterList
              state="ready"
              items={meters.data.items}
              houses={meters.data.houses}
              cancellable={meters.data.cancellable}
              selectedHouse={search.haus}
              selectedMeter={search.zaehler}
              showsChips={!isDesktop}
              onAdd={() => {
                setIsCreateOpen(true)
              }}
            />
          )}
        </div>

        {hasDetail && (
          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">
            {detail.isPending ? (
              <MeterDetail state="loading" />
            ) : detail.isError ? (
              <MeterDetail
                state="error"
                error={detail.error}
                onRetry={() => {
                  void detail.refetch()
                }}
              />
            ) : (
              <MeterDetail
                state="ready"
                meter={detail.data}
                house={search.haus}
                documents={
                  <DocumentList
                    query={documents}
                    fixedSubjectId={detail.data.id}
                    fixedSubjectLabel={detail.data.label}
                    emptyTitle="Noch keine Dokumente."
                    emptyHint="Dokument verknüpfen."
                    addLabel="Dokument verknüpfen"
                    isAddSecondary
                    canAdd={detail.data.can_edit}
                    compact
                  />
                }
                onSupplyAdd={() => {
                  setSupplySheet({ mode: 'create' })
                }}
                onSupplyEdit={(contract) => {
                  setSupplySheet({ mode: 'edit', contract })
                }}
                onSaved={() => {
                  setConfirmation({ message: 'Änderungen gespeichert', testId: 'meter-edit-confirmation' })
                }}
                onArchived={() => {
                  // Der stillgelegte Zähler ist aus der Liste weg: zurück auf `/energie`.
                  setConfirmation({ message: 'Zähler stillgelegt.', testId: 'meter-archive-confirmation' })
                  void navigate({ search: (previous) => ({ ...previous, zaehler: undefined }) })
                }}
              />
            )}
          </div>
        )}
      </div>

      {detail.data !== undefined && (
        <SupplyContractSheets
          meter={detail.data}
          state={supplySheet}
          onClose={() => {
            setSupplySheet(null)
          }}
        />
      )}
      <MeterCreateSheet
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        {...(presetHouse === undefined ? {} : { presetHouse: { id: presetHouse.id, label: presetHouse.label } })}
      />
      <ConfirmationStrip
        message={confirmation?.message ?? null}
        onDismiss={() => {
          setConfirmation(null)
        }}
        testId={confirmation?.testId ?? 'meter-edit-confirmation'}
      />
    </section>
  )
}
